import { getAuth } from 'firebase/auth';
import { googleSearch } from './googleSearch';

// In production, Firebase Hosting rewrites /api/** to the FastAPI service on
// Cloud Run (see firebase.json), so same-origin relative paths just work.
// For local dev against `uvicorn --reload` directly, set this in .env.local.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Firebase Hosting's rewrite to Cloud Run has a hard ~60s request timeout
// that Hosting itself enforces and that isn't configurable - unrelated to
// Cloud Run's own (configurable) request timeout. The CPU-only VLM backend
// routinely takes well over that just to generate a reply (see
// backend/README.md), so /api/chat and /api/tts (and anything else that
// only exists on the Cloud Run service, not the subscription Function) call
// Cloud Run directly instead, skipping the Hosting rewrite entirely. Only
// /api/subscription/** stays on API_BASE_URL, since that one lives on a
// Firebase Function (see firebase.json) which doesn't have this problem.
// Set in .env.production; falls back to API_BASE_URL (Hosting) locally.
const CLOUD_RUN_API_BASE_URL = process.env.REACT_APP_CLOUD_RUN_API_BASE_URL || API_BASE_URL;

class ApiError extends Error {
  constructor(message, status, detail) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

function extractMessage(body) {
  if (!body) return null;
  if (typeof body.detail === 'string') return body.detail;
  if (Array.isArray(body.detail) && body.detail[0]?.msg) return body.detail[0].msg;
  return null;
}

// A cold backend (the CPU has to load the several-GB vision model into
// memory - see backend/app/services/llm.py) can take up to half a minute to
// come back to life, in two different ways: the connection itself can fail
// or come back 502/503/504 while the container isn't ready yet, OR the
// connection succeeds once it's already warm. Every request through
// request() below retries through the first case for as long as
// COLD_START_BUDGET_MS allows - the same treatment wheniseeusAI already
// gives /api/chat - so a workspace action (create/join/invite/mode/delete)
// hit right as the container was scaling up from zero doesn't just fail as
// a 502 the caller has to notice and retry by hand.
const COLD_START_BUDGET_MS = 60 * 60 * 1000;
const RETRY_BACKOFF_MS = [3000, 5000, 8000, 12000, 20000, 30000];
const RETRYABLE_HTTP_STATUSES = new Set([502, 503, 504]);
const backoffFor = (attempt) => RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)];

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

async function request(path, options = {}, baseUrl = API_BASE_URL) {
  const auth = getAuth();
  const deadline = Date.now() + COLD_START_BUDGET_MS;

  for (let attempt = 0; ; attempt++) {
    // Fetched fresh on every attempt, not just once before the loop - a cold
    // start can keep this loop retrying for a long time (up to
    // COLD_START_BUDGET_MS), and reusing the token grabbed at the start
    // means it can expire mid-retry, so the request that finally reaches a
    // warm backend gets rejected with a 401 even though the user never
    // actually got logged out. getIdToken() returns the SDK's cached token
    // unless it's stale, so this is cheap when nothing needs refreshing.
    const currentUser = auth.currentUser;
    const token = currentUser ? await currentUser.getIdToken() : null;

    let response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(options.headers || {})
        }
      });
    } catch (networkError) {
      if (Date.now() >= deadline) throw networkError;
      await delay(backoffFor(attempt));
      continue;
    }

    if (!response.ok && RETRYABLE_HTTP_STATUSES.has(response.status) && Date.now() < deadline) {
      await delay(backoffFor(attempt));
      continue;
    }

    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new ApiError(extractMessage(body) || 'Request failed', response.status, body?.detail);
    }
    return body;
  }
}

// Browser geolocation for questions that need a place (weather, current
// time) - see backend/app/services/weather_service.py and time_service.py.
// Only requested for messages that look like they need it, so the permission
// prompt shows up in context ("¿qué clima hace?") rather than on the first
// message of every session. Cached for 10 minutes; null if the browser
// doesn't support it, the user denies it, or it takes too long - the backend
// then just answers without it.
const LOCATION_NEEDED_RE = /\b(clima|tiempo (hace|va a hacer|har[aá])|el tiempo (hoy|mañana|en)|hace (fr[ií]o|calor)|temperatura|llov(er|er[aá]|iendo|izna)|lluvia|nevar[aá]?|pron[oó]stico|paraguas|humedad|qu[eé] hora|qu[eé] d[ií]a es|qu[eé] fecha|weather|forecast|temperature|rain(ing|y)?|umbrella|humidity|how (hot|cold) is it|what time|what day is it|today'?s date)/i;
const LOCATION_CACHE_MS = 10 * 60 * 1000;
let cachedLocation = null;

function getUserLocation() {
  if (cachedLocation && Date.now() - cachedLocation.at < LOCATION_CACHE_MS) {
    return Promise.resolve(cachedLocation.coords);
  }
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);

  return new Promise((resolve) => {
    // getCurrentPosition's own timeout doesn't count time the permission
    // prompt sits unanswered - this does, so an ignored prompt can't stall
    // the message.
    const giveUp = setTimeout(() => resolve(null), 15000);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        clearTimeout(giveUp);
        const result = { lat: coords.latitude, lon: coords.longitude };
        cachedLocation = { coords: result, at: Date.now() };
        resolve(result);
      },
      () => {
        clearTimeout(giveUp);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: LOCATION_CACHE_MS }
    );
  });
}

// /api/chat streams newline-delimited JSON status events instead of one
// plain JSON body - {"status": "searching"}, {"status": "sources", "fuentes"}
// (as soon as a web search resolves, before generation starts) and/or
// {"status": "thinking"} while the backend works, then
// {"status": "done", "texto", "fuentes"} - so the UI can show
// "buscando en internet..." vs "pensando..." and the sites found while
// still generating (see App.js). onStatus receives the full event object
// for every non-final event; the promise resolves with the final
// { texto, fuentes } once "done" arrives.
export async function wheniseeusAI({
  mensaje, historial = [], imagenes = [], documentos = [], modoVoz = false, workspaceId = null, cerebro = false, signal, onStatus
}) {
  const auth = getAuth();

  // Resolved before the cold-start deadline starts ticking - the user may
  // spend a few seconds on the browser's permission prompt.
  const [ubicacion, resultadosWeb] = await Promise.all([
    LOCATION_NEEDED_RE.test(mensaje) ? getUserLocation() : null,
    // Group chats don't show Google's results box (see App.js's
    // GoogleResults), which Google's terms ask for - no browser search there.
    workspaceId ? [] : searchInBrowser(mensaje, historial, onStatus)
  ]);

  const deadline = Date.now() + COLD_START_BUDGET_MS;

  for (let attempt = 0; ; attempt++) {
    if (Date.now() >= deadline) {
      throw new ApiError('Request timed out', 504, null);
    }

    // Fetched fresh on every attempt - see the identical comment in
    // request() above. A chat reply already stalling on a cold backend is
    // exactly the case where this matters most, since it's the slowest
    // request in the app.
    const currentUser = auth.currentUser;
    const token = currentUser ? await currentUser.getIdToken() : null;

    // Own AbortController per attempt so a cold start that just sits there -
    // no error, no data, nothing - still gets cut off at the deadline instead
    // of hanging forever; wired to the caller's signal too, so tapping "stop"
    // still works exactly as before.
    const attemptController = new AbortController();
    const timeoutId = setTimeout(() => attemptController.abort(), deadline - Date.now());
    const onExternalAbort = () => attemptController.abort();
    signal?.addEventListener('abort', onExternalAbort, { once: true });
    const cleanup = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onExternalAbort);
    };

    let response;
    try {
      response = await fetch(`${CLOUD_RUN_API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ mensaje, historial, imagenes, documentos, modoVoz, workspaceId, cerebro, ubicacion, resultadosWeb }),
        signal: attemptController.signal
      });
    } catch (networkError) {
      cleanup();
      if (signal?.aborted) throw networkError; // the user tapped "stop" - not a timeout, let it propagate as-is
      if (Date.now() < deadline) {
        await delay(backoffFor(attempt), signal);
        continue;
      }
      throw new ApiError('Request timed out', 504, null);
    }

    if (!response.ok) {
      cleanup();
      if (RETRYABLE_HTTP_STATUSES.has(response.status) && Date.now() < deadline) {
        await delay(backoffFor(attempt), signal);
        continue;
      }
      const body = await response.json().catch(() => null);
      throw new ApiError(extractMessage(body) || 'Request failed', response.status, body?.detail);
    }

    try {
      const result = await readChatStream(response, onStatus);
      cleanup();
      return { ...result, buscoEnGoogle: resultadosWeb.length > 0 };
    } catch (streamError) {
      cleanup();
      if (signal?.aborted) throw streamError; // the user tapped "stop"
      if (Date.now() < deadline) {
        await delay(backoffFor(attempt), signal);
        continue;
      }
      throw new ApiError('Request timed out', 504, null);
    }
  }
}

// The backend says whether the message needs a web search and for what
// (/api/chat/busqueda), and the search runs here with Google's search
// element (see googleSearch.js) - Google's search API for servers is closed
// to new projects. Emits the same "searching"/"sources" statuses the
// backend's own search does. [] when no search is needed or it fails.
async function searchInBrowser(mensaje, historial, onStatus) {
  let plan;
  try {
    plan = await request('/chat/busqueda', {
      method: 'POST',
      body: JSON.stringify({ mensaje: mensaje.slice(0, 4000), historial })
    }, CLOUD_RUN_API_BASE_URL);
  } catch {
    return [];
  }
  if (!plan?.buscar || !plan.consulta) return [];

  onStatus?.({ status: 'searching' });
  const resultados = await googleSearch(plan.consulta);
  if (resultados.length) {
    onStatus?.({ status: 'sources', fuentes: resultados.map(({ title, link }) => ({ title, link })) });
  }
  return resultados;
}

async function readChatStream(response, onStatus) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    let newlineIndex;
    while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);
      if (!line.trim()) continue;

      const event = JSON.parse(line);
      if (event.status === 'done') {
        return { texto: event.texto, fuentes: event.fuentes };
      }
      onStatus?.(event);
    }
  }

  throw new ApiError('Request failed', 500, null);
}

export function verificarSuscripcionPaypal({ subscriptionID }) {
  return request('/subscription/verify', {
    method: 'POST',
    body: JSON.stringify({ subscriptionID })
  });
}

export function cancelarSuscripcionPaypal() {
  return request('/subscription/cancel', { method: 'POST' });
}

export function enviarConsultaSoporte({ nombre, email, mensaje }) {
  return request(
    '/support',
    { method: 'POST', body: JSON.stringify({ nombre, email, mensaje }) },
    CLOUD_RUN_API_BASE_URL
  );
}

// Fire-and-forget internal notification right after a successful signup
// (see App.js's handleSubmitAuth) - the backend itself never fails this
// back to the caller (see routers/support.py), but this is called without
// awaiting the error path mattering either way, so a network hiccup here
// never gets in the way of the new account actually working.
export function notificarRegistro() {
  return request('/notify-signup', { method: 'POST' }, CLOUD_RUN_API_BASE_URL);
}

// Workspaces (group chat foundation) - creation and membership writes go
// through the backend (Admin SDK) rather than direct Firestore writes, see
// firestore.rules and backend/app/services/workspaces.py. Reading a
// workspace's own doc/members is still direct client-side Firestore, since
// membership there only grants read access.
export function crearWorkspace({ name }) {
  return request('/workspaces', { method: 'POST', body: JSON.stringify({ name }) }, CLOUD_RUN_API_BASE_URL);
}

export function actualizarModoWorkspace({ workspaceId, mode }) {
  return request(
    `/workspaces/${workspaceId}/mode`,
    { method: 'PATCH', body: JSON.stringify({ mode }) },
    CLOUD_RUN_API_BASE_URL
  );
}

// Sends the confirmation-code email to `email` (admin-only, see
// backend/app/services/workspaces.py) - the code itself only ever leaves
// the backend via that email, so it isn't part of this response's callers'
// concern beyond showing/sharing it as a fallback.
export function invitarAWorkspace({ workspaceId, email }) {
  return request(
    `/workspaces/${workspaceId}/invites`,
    { method: 'POST', body: JSON.stringify({ email }) },
    CLOUD_RUN_API_BASE_URL
  );
}

export function unirseAWorkspace({ code }) {
  return request('/workspaces/join', { method: 'POST', body: JSON.stringify({ code }) }, CLOUD_RUN_API_BASE_URL);
}

export function eliminarWorkspace({ workspaceId }) {
  return request(`/workspaces/${workspaceId}`, { method: 'DELETE' }, CLOUD_RUN_API_BASE_URL);
}

// API keys de integración (ej. el Workspace IA de Skach). Van por el baseUrl
// por defecto ('/api'): en dev el proxy las manda al backend local, en
// producción Firebase Hosting reescribe /api/** al servicio FastAPI.
export function crearApiKey({ label = '' } = {}) {
  return request('/keys', { method: 'POST', body: JSON.stringify({ label }) });
}

export function listarApiKeys() {
  // no-store: la lista debe reflejar altas/bajas al instante, sin caché.
  return request('/keys', { method: 'GET', cache: 'no-store' });
}

export function revocarApiKey({ id }) {
  return request(`/keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function synthesizeSpeech({ mensaje, voz }) {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const token = currentUser ? await currentUser.getIdToken() : null;

  const response = await fetch(`${CLOUD_RUN_API_BASE_URL}/tts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ texto: mensaje, voz })
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(extractMessage(body) || 'TTS request failed', response.status, body?.detail);
  }

  return response.blob();
}

// One recorded utterance of the voice conversation -> its text (Whisper on
// the backend, see backend/app/routers/stt.py). A 503 means voice input is
// unavailable right now.
export async function transcribeSpeech(blob, language) {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const token = currentUser ? await currentUser.getIdToken() : null;

  const response = await fetch(`${CLOUD_RUN_API_BASE_URL}/stt?lang=${language === 'en' ? 'en' : 'es'}`, {
    method: 'POST',
    headers: {
      'Content-Type': blob.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: blob
  });

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(extractMessage(body) || 'STT request failed', response.status, body?.detail);
  }
  return (body?.texto || '').trim();
}
